import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import Loading from "../../Shared/Loading";

const SuperAdminViewOrder = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrderById = async () => {
      try {
        const response = await axios.get(
          `https://servercareseebd.careseebd.com/api/v1/orders/${id}`
        );
        setOrder(response.data);
        setSelectedStatus(response.data?.status || "Pending");
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching order:", error);
        setIsLoading(false);
      }
    };

    fetchOrderById();
  }, [id]);

  const handleStatusChange = async () => {
    try {
      await axios.put(`https://servercareseebd.careseebd.com/api/v1/orders/${id}`, {
        status: selectedStatus,
      });

      const response = await axios.get(
        `https://servercareseebd.careseebd.com/api/v1/orders/${id}`
      );
      setOrder(response.data);
      toast.success("Order status updated successfully!");
      navigate(-1);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Error updating order status!");
    }
  };

  const formatDate = (timestamp) => {
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };
    return new Date(timestamp).toLocaleDateString("en-US", options);
  };

  if (isLoading) {
    return (
      <div className="py-20 flex justify-center items-center">
        <Loading />
      </div>
    );
  }

  const clientName = order?.name || (order?.firstName ? `${order.firstName} ${order.lastName || ""}` : "N/A");
  const clientEmail = order?.email || order?.emailAddress || "N/A";

  return (
    <div className="max-w-6xl mx-auto select-none space-y-6">
      {/* Header bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(-1)}
              className="p-1.5 hover:bg-slate-50 text-slate-400 hover:text-slate-650 rounded-lg border border-slate-200 cursor-pointer transition-all mr-1"
            >
              <Icon icon="solar:arrow-left-outline" className="w-4 h-4" />
            </button>
            <h1 className="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
              <Icon icon="solar:file-text-bold" className="text-indigo-500 w-6 h-6" />
              Invoice Summary
            </h1>
          </div>
          <p className="text-[10px] text-slate-400 font-bold mt-1.5 uppercase tracking-wider">
            Order Reference: <span className="text-indigo-600 font-black">{order?._id}</span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wide border ${order?.status === "Confirm"
              ? "bg-emerald-50 text-emerald-700 border-emerald-100"
              : order?.status === "Reject"
                ? "bg-rose-50 text-rose-750 border-rose-100"
                : "bg-amber-50 text-amber-700 border-amber-100"
              }`}
          >
            {order?.status}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Cart items list */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4">
            <h2 className="text-xs font-black text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
              <Icon icon="solar:cart-large-2-bold" className="w-4 h-4 text-indigo-500" />
              Customer Cart Contents
            </h2>
            <div className="divide-y divide-slate-100">
              {order?.cartItems?.map((item) => (
                <div key={item._id} className="py-4 first:pt-0 last:pb-0 flex items-center gap-4">
                  {item?.imageUrl && (
                    <div className="w-16 h-16 rounded-xl border border-slate-100 bg-slate-50 p-1 flex items-center justify-center overflow-hidden flex-shrink-0">
                      <img className="max-w-full max-h-full object-contain" src={item?.imageUrl} alt={item?.name} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-xs font-bold text-slate-800 truncate">{item?.name}</h4>
                    <p className="text-[10px] text-slate-400 font-semibold mt-1">Weight: {item?.weight || "N/A"}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-black text-slate-800">৳ {item?.price * item?.quantity}</p>
                    <p className="text-[10px] text-slate-400 font-semibold mt-1">৳ {item?.price} × {item?.quantity}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing summary */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4">
            <h2 className="text-xs font-black text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
              <Icon icon="solar:wallet-money-bold" className="w-4 h-4 text-indigo-500" />
              Payment Summary
            </h2>
            <div className="space-y-2.5">
              <div className="flex justify-between text-xs font-medium text-slate-500">
                <span>Subtotal</span>
                <span>৳ {order?.subtotal}</span>
              </div>
              <div className="flex justify-between text-xs font-medium text-slate-500">
                <span>Tax Charge</span>
                <span>৳ {order?.tax}</span>
              </div>
              <div className="pt-2.5 border-t border-slate-100 flex justify-between text-xs font-black text-slate-800">
                <span>Total Amount</span>
                <span className="text-indigo-650">৳ {order?.total}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Customer and billing details sidebar */}
        <div className="space-y-6">
          {/* Customer profile card */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4">
            <h2 className="text-xs font-black text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
              <Icon icon="solar:user-bold" className="w-4 h-4 text-indigo-500" />
              Customer Profile
            </h2>
            <div className="space-y-3">
              <div>
                <p className="text-[10px] font-bold text-slate-450 uppercase tracking-wider">Full Name</p>
                <p className="text-xs font-bold text-slate-800 mt-1">{clientName}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-450 uppercase tracking-wider">Email Address</p>
                <p className="text-xs font-medium text-slate-600 mt-1 truncate">{clientEmail}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-450 uppercase tracking-wider">Contact Phone</p>
                <p className="text-xs font-medium text-slate-600 mt-1">{order?.phone || "N/A"}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-455 uppercase tracking-wider">Purchase Timestamp</p>
                <p className="text-xs font-medium text-slate-605 mt-1">{formatDate(order?.createdAt)}</p>
              </div>
            </div>
          </div>

          {/* Delivery location */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4">
            <h2 className="text-xs font-black text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
              <Icon icon="solar:map-point-wave-bold" className="w-4 h-4 text-indigo-500" />
              Delivery Details
            </h2>
            <div className="space-y-3">
              <div>
                <p className="text-[10px] font-bold text-slate-450 uppercase tracking-wider">Shipping Address</p>
                <p className="text-xs font-medium text-slate-650 mt-1.5 leading-relaxed">
                  {order?.streetAddress}
                  {order?.apartment && `, Apt ${order?.apartment}`}
                  <br />
                  {order?.townCity && `${order?.townCity}`}
                  {order?.state && `, ${order?.state}`}
                  {order?.pinCode && ` - ${order?.pinCode}`}
                  <br />
                  {order?.country}
                </p>
              </div>
              {order?.companyName && (
                <div>
                  <p className="text-[10px] font-bold text-slate-450 uppercase tracking-wider">Company</p>
                  <p className="text-xs font-semibold text-slate-700 mt-1">{order?.companyName}</p>
                </div>
              )}
              {order?.orderNotes && (
                <div>
                  <p className="text-[10px] font-bold text-slate-455 uppercase tracking-wider">Notes</p>
                  <p className="text-xs text-slate-500 mt-1.5 italic leading-relaxed">"{order?.orderNotes}"</p>
                </div>
              )}
            </div>
          </div>

          {/* Manage status widget */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4">
            <h2 className="text-xs font-black text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
              <Icon icon="solar:settings-bold" className="w-4 h-4 text-indigo-500" />
              Change Order Status
            </h2>
            <div className="space-y-2">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full p-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-bold bg-slate-50"
              >
                <option value="Pending">Pending</option>
                <option value="Confirm">Confirm</option>
                <option value="Reject">Reject</option>
              </select>
              <button
                onClick={handleStatusChange}
                className="w-full py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-95 text-white font-extrabold text-xs rounded-xl shadow-md shadow-cyan-500/10 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
              >
                Update Status
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminViewOrder;
